<template>
  <div
    :class="{ 'has-error': hasAnyErrors }"
    class="FormField"
  >
    <slot name="label">
      <div
        v-if="(label && !hideLabel) || tooltip"
        class="mb-1 flex items-center"
      >
        <label
          v-if="label && !hideLabel"
          :class="[{ 'text-brand': hasAnyErrors }, labelClasses]"
          class="FormFieldLabel block font-sans text-xs font-normal uppercase tracking-wide text-muted"
        >
          {{ label }}

          <span
            v-show="!isRequired"
            :data-testid="`${attribute}-validation-rule`"
            class="lowercase text-muted-faint"
          >
            {{ $t('forms.optional') }}
          </span>
        </label>

        <el-tooltip
          v-if="tooltip"
          placement="top"
        >
          <span class="ml-auto text-muted">
            <i class="hf hf-question-circle" />
          </span>

          <div slot="content" v-html="tooltip" />
        </el-tooltip>
      </div>
    </slot>

    <slot
      :errors="activeErrors"
      :has-errors="hasAnyErrors"
      :first-error-message="firstErrorMessage"
      :validator="preferredValidator"
      name="control"
    >
      <div class="FormField__Control relative">
        <slot
          :errors="activeErrors"
          :has-errors="hasAnyErrors"
          :first-error-message="firstErrorMessage"
          :classes="computedInputClasses"
          :validator="preferredValidator"
          :placeholder="resolvedAttribute"
          :attrs="{ name, class: computedInputClasses }"
        />

        <span
          v-if="leftIcon || $slots.leftIcon"
          :class="[{ 'text-brand': hasAnyErrors }, iconClass]"
          class="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-muted"
        >
          <slot name="leftIcon">
            <v-icon :type="leftIcon" />
          </slot>
        </span>

        <span
          v-if="rightIcon"
          :class="{ 'text-brand': hasAnyErrors }"
          class="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-muted"
        >
          <i :class="rightIcon" class="hf" />
        </span>
      </div>
    </slot>

    <slot
      :errors="activeErrors"
      :has-errors="hasAnyErrors"
      :first-error-message="firstErrorMessage"
      name="errors"
    >
      <template v-if="hasErrors">
        <p
          v-if="showSingleError"
          class="mt-1 font-sans text-sm text-brand"
        >
          {{ firstErrorMessage }}
        </p>

        <template v-if="!showSingleError">
          <p
            v-for="error in activeErrors"
            :key="error.validationKey"
            :data-validation-attr="error.validationKey"
            class="mt-1 font-sans text-sm text-brand"
          >
            {{ getErrorMessage(error.validationKey, error.params) }}
          </p>
        </template>
      </template>

      <template v-if="serverErrors">
        <template v-if="Array.isArray(serverErrors)">
          <p
            v-for="error in serverErrors"
            :key="error"
            class="mt-1 font-sans text-sm text-brand"
          >
            {{ error.detail || error }}
          </p>
        </template>

        <p v-else class="mt-1 font-sans text-sm text-brand">
          {{ serverErrors }}
        </p>
      </template>
    </slot>
  </div>
</template>

<script>
import { singleErrorExtractorMixin } from 'vuelidate-error-extractor'

// House input recipe (mirrors settings/fieldErrorMixin.js so FormField-driven inputs
// and the Vuelidate-without-FormField forms stay visually identical).
const BASE_INPUT = 'h-10 w-full rounded-lg border border-solid px-3 font-sans text-sm text-ink outline-none transition-colors focus:border-accent'

export default {
  name: 'FormField',
  mixins: [singleErrorExtractorMixin],
  props: {
    leftIcon: {
      type: String,
      default: ''
    },
    rightIcon: {
      type: String,
      default: ''
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    serverErrors: {
      type: [Array, String],
      default: () => []
    },
    tooltip: {
      type: String,
      default: ''
    },
    forceError: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    labelClasses: {
      type: [String, Object],
      default: ''
    },
    iconClass: {
      type: String,
      default: ''
    }
  },

  computed: {
    isRequired () {
      return this.required || Object.keys(this.preferredValidator)
        .some(key => key.includes('required'))
    },

    hasAnyErrors () {
      return (
        this.forceError ||
        this.hasErrors ||
        (this.serverErrors && this.serverErrors.length > 0)
      )
    },

    computedInputClasses () {
      return `${BASE_INPUT} ${this.hasAnyErrors ? 'border-brand' : 'border-line'}`
    }
  }
}
</script>

<style lang="scss">
// De-Bulma'd (S20): no `~utils` import, no Bulma `$vars`. `#bf0000` is the `brand`
// token value, used here only to redden the border of non-house controls (Element
// `el-input`/`el-select`, plain native inputs) that don't consume `computedInputClasses`.
.FormField {
  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }

  &.has-error {
    .el-input__inner,
    .el-textarea__inner,
    input:not([type='checkbox']):not([type='radio']),
    textarea {
      border-color: #bf0000;
    }
  }
}
</style>
